import classNames from 'classnames'
import getConfig from 'next/config'
import { createElement } from 'react'
import CustomImage from 'src/components/Image'
import { FaqItem } from 'src/types'
import { filterRegex } from 'src/utils/carriers_info'
import styles from './index.module.scss'

const { publicRuntimeConfig } = getConfig()

const assetPrefix = publicRuntimeConfig.assetPrefix || ''

// #region component
interface Props {
  children?: any[]
  qaList: FaqItem[]
}

interface InnerProps {
  content: any
  uiType?: string
  index?: number
  isStrong?: boolean
}

export default function FaqList({ qaList, children = [] }: Props) {
  if (!qaList?.length && !children?.length) return null
  return (
    <div className={classNames(styles.seoFaqBlock)} itemScope itemType="https://schema.org/FAQPage">
      {qaList?.map(({ title, uiType, content }, index) => {
        return (
          <div
            key={index}
            className="flex flex-column gap-[8px]"
            itemProp="mainEntity"
            itemScope
            itemType="https://schema.org/Question"
          >
            {createElement(
              uiType || 'h2',
              { key: `title-${index}`, id: filterRegex(title, /[ ?]/g, '-'), itemProp: 'name' },
              title
            )}
            <div key={`answer-${index}`} itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              {typeof content === 'string' ? (
                <div itemProp="text" className={styles.answerContent} dangerouslySetInnerHTML={{ __html: content }} />
              ) : (
                <div itemProp="text" className={styles.answerContent}>
                  <RenderContent content={content} />
                </div>
              )}
            </div>
          </div>
        )
      })}
      {children}
    </div>
  )
}

function RenderContent({ content }: any) {
  const contentType = Object.prototype.toString.call(content).slice(8, -1)
  switch (contentType) {
    case 'Object': {
      const { title, uiType, ...others } = content
      return (
        <>
          {title ? <h3>{title}</h3> : null}
          {uiType ? <InnerContent uiType={uiType} {...others} /> : <RenderContent {...others} />}
        </>
      )
    }
    case 'Array':
      return content.map((item, index) => <RenderContent key={index} content={item} />)
    default:
      return <div dangerouslySetInnerHTML={{ __html: content }} />
  }
}

function InnerContent({ uiType, content, ...others }: InnerProps) {
  switch (uiType) {
    case 'p': {
      const { isStrong } = others
      if (isStrong) {
        return (
          <p style={{ marginTop: '12px' }}>
            <strong>{content}</strong>
          </p>
        )
      }
      return <p>{content}</p>
    }
    case 'ul':
      return (
        <ul>
          {content.map((item: any, i: number) => (
            <li
              key={`ul-${i}`}
              dangerouslySetInnerHTML={{
                __html: item.content || item
              }}
            />
          ))}
        </ul>
      )
    case 'ol':
      return (
        <ol>
          {content.map((item: any, i: number) => (
            <li
              key={`ul-${i}`}
              dangerouslySetInnerHTML={{
                __html: item.content || item
              }}
            />
          ))}
        </ol>
      )
    case 'img': {
      const { path, className, ...others } = content
      return (
        <CustomImage
          src={`${assetPrefix}/assets/images/carriers/carrier_info/${path}`}
          loading="lazy"
          decoding="async"
          fill
          className={classNames('lazy-image-fill', className)}
          {...others}
        />
      )
    }
    case 'table': {
      const { columns, rows } = content
      return (
        <table>
          <thead>
            <tr>
              {columns.map((header: string, index: number) => (
                <th key={`table-th-${index}`} rowSpan={1} colSpan={1}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row: string[], rowIndex: number) => (
              <tr key={`table-tr-${rowIndex}`}>
                {row.map((cell: string, cellIndex: number) => (
                  <td key={`table-td-${rowIndex}-${cellIndex}`} rowSpan={1} colSpan={1}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )
    }
    default:
      return <div dangerouslySetInnerHTML={{ __html: content }} />
  }
}
